/**
 * SimpleDetail — Simple view's expense detail (#476), opened from a name in
 * This month. One primary action (Mark as paid); Add receipt keeps a slip
 * with the expense; More… is a view change: it switches the account to the
 * full view and opens that expense's full card there.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useRef, useState } from 'react';
import { api } from '../../api.js';
import { spentOf, leftOf } from '../../money.js';
import { useAccordion } from '../AccordionRegister.jsx';
import { Icon, rand, showViewToast, useViewMode } from './viewMode.jsx';

export default function SimpleDetail({ item, paid, onTick, refresh, onBack }) {
  const acc = useAccordion();
  const { setMode } = useViewMode();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const amount = Number(item.amount) || 0;
  const spent = spentOf(item);
  const pct = amount > 0 ? Math.min(100, Math.round((spent / amount) * 100)) : 0;

  const addReceipt = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('receipt', f);
      await api(`/budget/items/${item.id}/receipt/`, { method: 'POST', form: fd });
      showViewToast(`Receipt kept with ${item.title}.`);
      refresh();
    } catch {
      showViewToast('Could not save the receipt — try again.');
    } finally { setBusy(false); }
  };

  // Open the full card (the dashboard row register), then leave Simple view.
  const more = () => {
    acc.open(`exp:${item.id}`);
    setMode('full', {
      toast: `Showing everything — the full ${item.title} card is open.`,
      then: () => document.querySelector(`[data-item-id="${item.id}"]`)?.scrollIntoView({ block: 'center' }),
    });
  };

  return (
    <div className="sv-form sv-fade">
      <button type="button" className="sv-ghost sv-back" onClick={onBack}>{Icon.back}Home</button>
      <h1 className="sv-h1 big">{item.title}</h1>
      <div className="sv-status" style={{ color: paid ? 'var(--sv-a300)' : 'color-mix(in srgb, var(--sv-text) 72%, transparent)' }}>
        {paid ? 'Paid ✓' : 'Not paid yet'}
      </div>
      <div className="sv-figures">
        <div className="sv-figrow"><span>Amount</span><span>{rand(amount)}</span></div>
        <div className="sv-figrow"><span>Spent so far</span><span>{rand(spent)}</span></div>
        <div className="sv-figrow"><span>Left to spend</span><span>{rand(leftOf(item))}</span></div>
        <div className="sv-bar" role="progressbar" aria-label="Spent so far" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
          <div style={{ width: `${pct}%` }} />
        </div>
      </div>
      {paid ? (
        <button type="button" className="sv-secondary sv-quiet" onClick={onTick}>{Icon.undo}Mark as not paid</button>
      ) : (
        <button type="button" className="sv-primary" onClick={onTick}>{Icon.check}Mark as paid</button>
      )}
      <input ref={fileRef} type="file" accept="image/*,application/pdf" capture="environment" hidden onChange={addReceipt} />
      <button type="button" className="sv-secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
        {busy ? Icon.spinner : Icon.camera}Add receipt
      </button>
      <button type="button" className="sv-ghost stack" onClick={more}>
        More…<span className="sv-ghost-sub">Opens the full card in full view</span>
      </button>
    </div>
  );
}
